import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, type EdgeInfo, type EdgeMetrics, type EdgeDisk, type LatestRelease } from '../lib/api';
import {
  RefreshCw, GitCommit, ExternalLink, CheckCircle2, AlertTriangle, Server,
  Cpu, MemoryStick, HardDrive, Activity,
} from 'lucide-react';

import { GITHUB_REPO, formatRelative, heartbeatColor, isMetricsStale } from '../lib/edgeStatus';
import { EdgeReleaseBadge } from '../components/EdgeReleaseStatus';

/** Tailwind fill class for a 0-100 utilization bar. Muted when stale. */
function utilColor(pct: number, stale: boolean): string {
  if (stale) return 'bg-slate-400';
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 75) return 'bg-amber-500';
  return 'bg-emerald-500';
}

export function formatGB(gb: number): string {
  if (!Number.isFinite(gb)) return '—';
  return gb >= 100 ? `${Math.round(gb)}` : gb.toFixed(1);
}

/**
 * Usage at which a filesystem is called out rather than just shaded. Matches the
 * edge's own EDGE_DISK_WARN_PCT default, so the portal and the box's log agree.
 * At/above this we show REMAINING space instead of used/total: 720FrontRdNew sat
 * at "92/98G" for seven weeks and nobody read it as an emergency, where "1.5G
 * free" is unmissable.
 */
export const DISK_WARN_PCT = 85;

/** Text tint for a disk readout: red past the bar's red threshold, amber at the warn line. */
export function diskTextColor(pct: number, stale: boolean): string {
  if (stale) return 'text-slate-500';
  if (pct >= 90) return 'text-red-600';
  if (pct >= DISK_WARN_PCT) return 'text-amber-600';
  return '';
}

/** `1.5G free` once a disk is under pressure, else the familiar `92/98G`. */
export function diskReadout(d: EdgeDisk): string {
  if (d.freeGB != null && d.pct >= DISK_WARN_PCT) return `${formatGB(d.freeGB)}G free`;
  return `${formatGB(d.usedGB)}/${formatGB(d.totalGB)}G`;
}

// Edge↔relay round-trip color thresholds (home internet varies, so be lenient).
export function rttColor(ms: number, stale: boolean): string {
  if (stale) return 'text-slate-500';
  if (ms >= 200) return 'text-red-600';
  if (ms >= 80) return 'text-amber-600';
  return 'text-emerald-600';
}

export function MetricBar({ pct, stale }: { pct: number; stale: boolean }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
      <div
        className={`h-full rounded-full ${utilColor(clamped, stale)}`}
        style={{ width: `${stale ? 0 : clamped}%` }}
      />
    </div>
  );
}

function EdgeResources({ metrics }: { metrics?: EdgeMetrics }) {
  if (!metrics) {
    return <span className="text-slate-500 text-xs">—</span>;
  }
  const stale = isMetricsStale(metrics.at);
  const memPct =
    metrics.memTotalMB > 0
      ? (metrics.memUsedMB / metrics.memTotalMB) * 100
      : 0;
  const memUsedGB = metrics.memUsedMB / 1024;
  const memTotalGB = metrics.memTotalMB / 1024;
  const textTint = stale ? 'text-slate-500' : 'text-ink-800';
  // Root first, then external/recordings drives.
  const disks: EdgeDisk[] = [...(metrics.disks ?? [])].sort(
    (a, b) => Number(a.external) - Number(b.external),
  );

  return (
    <div
      className={`flex flex-col gap-1.5 min-w-[170px] ${stale ? 'opacity-60' : ''}`}
      title={`Sampled ${formatRelative(metrics.at)}${stale ? ' (stale)' : ''}`}
    >
      {/* CPU */}
      <div className="flex items-center gap-2">
        <Cpu size={11} className="text-slate-500 shrink-0" />
        <div className="flex-1">
          <MetricBar pct={metrics.cpuPct} stale={stale} />
        </div>
        <span className={`text-[10px] tabular-nums w-16 text-right ${textTint}`}>
          {stale ? '—' : `${Math.round(metrics.cpuPct)}%`}
          <span className="text-slate-400"> · {metrics.load1.toFixed(1)}</span>
        </span>
      </div>

      {/* Memory */}
      <div className="flex items-center gap-2">
        <MemoryStick size={11} className="text-slate-500 shrink-0" />
        <div className="flex-1">
          <MetricBar pct={memPct} stale={stale} />
        </div>
        <span className={`text-[10px] tabular-nums w-16 text-right ${textTint}`}>
          {stale ? '—' : `${formatGB(memUsedGB)}/${formatGB(memTotalGB)}G`}
        </span>
      </div>

      {/* Edge↔relay latency: TCP round-trip to frps — the hop the app's off-LAN traffic traverses */}
      {metrics.frpsRttMs != null && (
        <div className="flex items-center gap-2" title="TCP round-trip from the edge to the frps relay (the app's off-LAN path)">
          <Activity size={11} className="text-slate-500 shrink-0" />
          <div className="flex-1">
            <span className="text-[9px] uppercase tracking-wider text-slate-500">relay rtt</span>
          </div>
          <span className={`text-[10px] tabular-nums w-16 text-right ${rttColor(metrics.frpsRttMs, stale)}`}>
            {stale ? '—' : `${metrics.frpsRttMs} ms`}
          </span>
        </div>
      )}

      {/* Disks (root + external/recordings) */}
      {disks.map((d) => (
        <div key={d.mount} className="flex items-center gap-2">
          <HardDrive
            size={11}
            className={`shrink-0 ${d.external ? 'text-slate-500' : 'text-slate-500'}`}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span
                className={`text-[9px] uppercase tracking-wider truncate ${
                  d.external ? 'text-slate-600' : 'text-slate-500'
                }`}
                title={
                  `${d.label} (${d.mount})${d.external ? ' · external/recordings' : ''}`
                  + ` — ${formatGB(d.usedGB)} of ${formatGB(d.totalGB)} GB used, ${Math.round(d.pct)}%`
                  + (d.freeGB != null ? `, ${formatGB(d.freeGB)} GB free` : '')
                }
              >
                {d.label}
                {d.external && (
                  <span className="ml-1 normal-case tracking-normal text-slate-500">·rec</span>
                )}
              </span>
            </div>
            <MetricBar pct={d.pct} stale={stale} />
          </div>
          <span
            className={`text-[10px] tabular-nums w-16 text-right ${
              stale ? textTint : diskTextColor(d.pct, stale) || textTint
            }`}
          >
            {stale ? '—' : diskReadout(d)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function EdgesPage() {
  const api = useApi();
  const [edges, setEdges] = useState<EdgeInfo[]>([]);
  const [latestRelease, setLatestRelease] = useState<LatestRelease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState('all');

  const refresh = async () => {
    try {
      const [{ edges }, release] = await Promise.all([
        api.listEdges(),
        api.getLatestRelease().catch(() => null),
      ]);
      setEdges(edges);
      if (release) setLatestRelease(release);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10000);
    return () => clearInterval(id);
  }, []);

  const healthy = edges.filter((e) => e.release?.state === 'healthy').length;
  const attention = edges.filter((e) => ['stalled', 'repair_required', 'quarantined', 'rollback'].includes(e.release?.state ?? '')).length;
  const offline = edges.filter((e) => e.release?.state === 'offline').length;
  const visibleEdges = stateFilter === 'all' ? edges : edges.filter((e) => e.release?.state === stateFilter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-brand-ink">Edges</h1>
          <p className="text-sm text-slate-600 mt-0.5">
            All edge boxes across the fleet. Sorted by most-recent heartbeat.
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-ink px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-400"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard
          label="Total"
          value={edges.length}
          icon={<Server size={14} className="text-slate-600" />}
        />
        <SummaryCard
          label="Healthy"
          value={healthy}
          tint={healthy > 0 ? 'emerald' : 'ink'}
          icon={<CheckCircle2 size={14} className="text-emerald-600" />}
        />
        <SummaryCard
          label="Needs attention"
          value={attention}
          tint={attention > 0 ? 'amber' : 'ink'}
          icon={<AlertTriangle size={14} className="text-amber-600" />}
        />
        <SummaryCard
          label="Offline"
          value={offline}
          tint={offline > 0 ? 'red' : 'ink'}
          icon={<span className="w-2 h-2 rounded-full bg-red-500 inline-block" />}
        />
      </div>

      {latestRelease?.manifest && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-white border border-slate-200 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <GitCommit size={12} className="text-slate-500" />
            <span>Latest published ({latestRelease.channel}):</span>
            <a
              href={`https://github.com/${GITHUB_REPO}/commit/${latestRelease.manifest.sha}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-brand-ink hover:text-slate-600 inline-flex items-center gap-1"
            >
              {latestRelease.manifest.shaShort} <ExternalLink size={10} />
            </a>
            <span className="italic text-slate-600 truncate max-w-md" title={latestRelease.manifest.subject}>
              "{latestRelease.manifest.subject}"
            </span>
          </div>
          {latestRelease.manifest.committedAt && (
            <span className="text-xs text-slate-500 shrink-0">
              Committed {formatRelative(latestRelease.manifest.committedAt)}
            </span>
          )}
        </div>
      )}

      <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
        <label htmlFor="edge-release-state">Release state</label>
        <select id="edge-release-state" value={stateFilter} onChange={(event) => setStateFilter(event.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-1 text-brand-ink">
          <option value="all">All</option>
          {['healthy', 'behind', 'applying', 'stalled', 'paused', 'offline', 'rollback', 'quarantined', 'repair_required', 'unknown'].map((state) =>
            <option key={state} value={state}>{state.replace('_', ' ')}</option>)}
        </select>
        <span>{visibleEdges.length} of {edges.length} visible</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Edge</th>
              <th className="text-left px-4 py-3 font-medium">SHA</th>
              <th className="text-left px-4 py-3 font-medium">Release state</th>
              <th className="text-left px-4 py-3 font-medium">Assignment</th>
              <th className="text-left px-4 py-3 font-medium">Resources</th>
              <th className="text-left px-4 py-3 font-medium">Paired homes</th>
              <th className="text-left px-4 py-3 font-medium">Deployed</th>
              <th className="text-left px-4 py-3 font-medium">Heartbeat</th>
              <th className="text-left px-4 py-3 font-medium">Public IP</th>
              <th className="text-left px-4 py-3 font-medium">LAN IP</th>
            </tr>
          </thead>
          <tbody>
            {edges.length === 0 && !loading && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-slate-600">
                  No edges paired yet.
                </td>
              </tr>
            )}
            {visibleEdges.map((e) => (
              <EdgeRow key={e.edgeId} edge={e} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({
  label, value, icon, tint = 'ink',
}: {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  tint?: 'ink' | 'emerald' | 'amber' | 'red';
}) {
  const tintMap: Record<string, string> = {
    ink: 'border-slate-200 bg-white text-brand-ink',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    red: 'border-red-200 bg-red-50 text-red-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tintMap[tint]}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-600 mb-1.5 inline-flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function EdgeRow({ edge: e }: { edge: EdgeInfo }) {
  return (
    <tr className="border-t border-slate-200 hover:bg-slate-50">
      <td className="px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-brand-ink inline-flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${heartbeatColor(e.lastHeartbeatAt)}`} />
            {e.hostname || <span className="text-slate-500">no hostname</span>}
          </span>
          <span className="text-[10px] text-slate-500 font-mono" title={e.edgeId}>
            {e.edgeId?.slice(0, 8)} {e.version ? `· ${e.version}` : ''}
          </span>
          {e.osVersion && (
            <span className="text-[10px] text-slate-400">{e.osVersion}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {e.currentSha ? (
          <a
            href={`https://github.com/${GITHUB_REPO}/commit/${e.currentSha}`}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-brand-ink hover:text-slate-600 inline-flex items-center gap-1"
            title={e.currentShaSubject}
          >
            {e.currentShaShort ?? e.currentSha.slice(0, 8)}
            <ExternalLink size={10} className="text-slate-500" />
          </a>
        ) : (
          <span className="text-slate-500 text-xs">—</span>
        )}
        {e.lastDeployReason === 'rollback' && (
          <span className="ml-2 text-[10px] text-amber-600 uppercase tracking-wider">rolled back</span>
        )}
        {e.lastDeployReason === 'containment-hold' && (
          <span className="ml-2 text-[10px] text-amber-600 uppercase tracking-wider">contained · needs attention</span>
        )}
      </td>
      <td className="px-4 py-3">
        <EdgeReleaseBadge release={e.release} />
        {e.release?.failureCode && <div className="text-[10px] font-mono text-slate-500 mt-1">{e.release.failureCode}</div>}
      </td>
      <td className="px-4 py-3 text-xs">
        <div className="font-mono">{e.release?.desired?.runtimeSha.slice(0, 8) ?? '—'}</div>
        <div className="text-slate-500">{e.release?.desired?.cohort ?? 'unassigned'}{e.release?.desired?.paused ? ' · paused' : ''}</div>
      </td>
      <td className="px-4 py-3 align-top">
        <EdgeResources metrics={e.metrics} />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-0.5">
          {(e.pairedHomes ?? []).map((hid) => (
            <Link
              key={hid}
              to={`/homes/${hid}`}
              className="text-xs font-mono text-slate-600 hover:text-brand-ink"
            >
              {hid}
            </Link>
          ))}
          {(e.pairedHomes?.length ?? 0) === 0 && (
            <span className="text-slate-500 text-xs">—</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-ink-800 text-xs">
        {e.lastDeployAt ? formatRelative(e.lastDeployAt) : '—'}
      </td>
      <td className="px-4 py-3 text-ink-800 text-xs">
        {formatRelative(e.lastHeartbeatAt)}
      </td>
      <td className="px-4 py-3 text-ink-800 text-xs font-mono">
        {e.lastPublicIp ?? '—'}
      </td>
      <td className="px-4 py-3 text-ink-800 text-xs font-mono">
        {e.metrics?.lanIp ?? '—'}
      </td>
    </tr>
  );
}
